import manifest from "../../../../config/questionnaire-capabilities.json";
import type { StudySpecExposureMode } from "./study-spec.generated";
import scaleWordingJson from "../../../../config/questionnaire-scale-wording.json";
import {
  BRAND_COMPETITOR_MAX,
  MATRIX_MAX_COLUMNS,
  RESEARCH_QUESTION_CEILING,
} from "./question-capabilities";

/**
 * What the builder cannot do, or does only approximately (S4.11), shared with
 * Python (`apps/python-api/models/questionnaire/capabilities.py`, which renders
 * the model-facing meanings). A request part names one as `capability`; the
 * builder words it in the interface's language from the id and these limits.
 * Every registry id: an entry's `stages` narrows only which stage's schema
 * offers it (a revision part never names `question_count`, a first build's may).
 */
export type CapabilityId = keyof typeof manifest.capabilities;

if (manifest.schemaVersion !== "questionnaire-capabilities.v1") {
  throw new Error("Unsupported questionnaire capability registry schema");
}

/** In registry order. */
export const CAPABILITY_IDS = Object.freeze(
  Object.keys(manifest.capabilities) as Array<CapabilityId>,
);

/**
 * The limits a capability's words name, from their owners: the grid column
 * cap, the question ceiling, the competitor cap and the survey languages (the scale-wording
 * packs' languages). The
 * builder names the supported rating scales itself, in the reader's language
 * (`ratingScaleUserName`).
 */
export const CAPABILITY_LIMITS = Object.freeze({
  matrixMaxColumns: MATRIX_MAX_COLUMNS,
  researchQuestionCeiling: RESEARCH_QUESTION_CEILING,
  brandCompetitorMax: BRAND_COMPETITOR_MAX,
  surveyLanguages: Object.freeze(Object.keys(scaleWordingJson)),
});


/**
 * The capabilities that have a nearest supported version (C06-1): the
 * registry states it once as a typed field, which the models read inside the
 * capability's meaning and the builder words for the user by the id. A
 * nearest part of one of these says what was done instead; any other nearest
 * part keeps its capability's words. A nearest that differs by the study's
 * exposure design states one line per design (an order of versions shown
 * together is false in an isolated design), and the builder words the line of
 * the design the turn committed under.
 */
type Registry = typeof manifest.capabilities;
export type CapabilityNearestId = {
  [K in keyof Registry]: Registry[K] extends { nearest: string | object } ? K : never;
}[keyof Registry];

type NearestEntry = { nearest?: string | Partial<Record<StudySpecExposureMode, string>> };

export const CAPABILITY_NEAREST_IDS: ReadonlySet<CapabilityNearestId> = new Set(
  (Object.entries(manifest.capabilities) as Array<[CapabilityNearestId, NearestEntry]>).flatMap(
    ([id, entry]) => (entry.nearest ? [id] : []),
  ),
);

/** Whether a capability declares what its nearest supported version does. */
export function capabilityHasNearest(
  capability: CapabilityId,
): capability is CapabilityNearestId {
  return (CAPABILITY_NEAREST_IDS as ReadonlySet<string>).has(capability);
}

/**
 * The key (under the builder's `capabilityNearest` lines) of what a nearest
 * part did, for the design the turn committed under: the id for one line for
 * every design, `<id>.<design>` for a line per design. Null when the
 * capability has none for that design (or the turn recorded none), and the
 * part then keeps its capability's words.
 */
export function capabilityNearestKey(
  capability: CapabilityId,
  exposure: StudySpecExposureMode | null | undefined,
): string | null {
  const nearest = (manifest.capabilities[capability] as NearestEntry).nearest;
  if (!nearest) return null;
  if (typeof nearest === "string") return capability;
  return exposure && nearest[exposure] ? `${capability}.${exposure}` : null;
}

/**
 * The key (under the builder's `capability` lines) of why a part was not done
 * as asked, for the design the turn committed under. A capability whose
 * nearest version differs by design has one line per such design and `any`
 * for a design with none (wave H M11, LP-23 T8, AD-15: the shared design's
 * "each can be shown on its own first" was told in an isolated study); any
 * other capability has one line.
 */
export function capabilityWordsKey(
  capability: CapabilityId,
  exposure: StudySpecExposureMode | null | undefined,
): string {
  const nearest = (manifest.capabilities[capability] as NearestEntry).nearest;
  if (!nearest || typeof nearest === "string") return capability;
  return exposure && nearest[exposure] ? `${capability}.${exposure}` : `${capability}.any`;
}

/** Every key `capabilityWordsKey` can return, for the builder's lines to be checked against. */
export const CAPABILITY_WORDS_KEYS: ReadonlyArray<string> = (
  Object.entries(manifest.capabilities) as Array<[CapabilityId, NearestEntry]>
).flatMap(([id, entry]) =>
  !entry.nearest || typeof entry.nearest === "string"
    ? [id]
    : ["any", ...Object.keys(entry.nearest)].map((design) => `${id}.${design}`),
);

/** Every key `capabilityNearestKey` can return, for the builder's lines to be checked against. */
export const CAPABILITY_NEAREST_KEYS: ReadonlyArray<string> = (
  Object.entries(manifest.capabilities) as Array<[CapabilityId, NearestEntry]>
).flatMap(([id, entry]) =>
  !entry.nearest
    ? []
    : typeof entry.nearest === "string"
      ? [id]
      : Object.keys(entry.nearest).map((design) => `${id}.${design}`),
);

/**
 * Whether a capability names a limit of the builder, as opposed to the reason
 * a part carries when nothing else fits (the registry's `reasonOnly`:
 * `no_chat_operation`, "a change no operation makes", and the code-set
 * `not_changed_this_turn`). A part whose operations ran is told as a nearest
 * version only for a limit: a reason that says no operation makes the change
 * contradicts the operations it lists (MT-08: three questions added, told as
 * "the chat can't do this").
 */
export function capabilityIsLimit(capability: CapabilityId): boolean {
  return (
    (manifest.capabilities[capability] as { reasonOnly?: boolean }).reasonOnly !==
    true
  );
}

/**
 * Whether a reason says the part asks for nothing to change (the registry's
 * `nothingToChange`: `no_change_needed`): to keep something, a check that finds
 * nothing to change, or an aim the questions already ask. Such a part names what
 * it is about (`unchanged`); Nest tells it as already so only when those exist
 * and the turn left them unchanged (`partOutcomes`).
 */
export function capabilityAsksNothingToChange(capability: CapabilityId): boolean {
  return (
    (manifest.capabilities[capability] as { nothingToChange?: boolean }).nothingToChange === true
  );
}

/**
 * Whether the upload card takes the file a part needing this capability names
 * (L-B2, the registry's typed `fileOnCard`): another picture of one asset is
 * a swap (BP-01), so its card is offered; a picture of several assets
 * (CT-13's line-up shot) or of items past the study's limit (PR-13's
 * packshots) has nowhere to go, so no card offers it and the part keeps its
 * reason. The models never read it.
 */
export function capabilityFileOnCard(capability: CapabilityId): boolean {
  return (
    (manifest.capabilities[capability] as { fileOnCard?: boolean }).fileOnCard ===
    true
  );
}
